import InlineBanner, { type InlineBannerVariant } from '@/components/UI/InlineBanner/InlineBanner';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { SendRunScope as RunScope, SendScopeState, SendTrcProxy } from '@/model/Agent.model';
import type { FC } from 'react';
import SendSeverityBadge from './SendSeverityBadge';
import {
  DECLARATION_LABEL,
  DECLARATION_STATUS_TEXT,
  declarationsOf,
  declaredText,
  SCOPE_REASON_TEXT,
  scopeHeadlineOf,
  TRC_OUTCOME_TEXT,
  type SendScopeStage,
} from '@/components/Agents/SendValidator/utils/sendScope';
import styles from './sendResults.module.scss';

const VARIANT: Record<SendScopeState, InlineBannerVariant> = {
  out_of_scope: 'critical',
  scope_unknown: 'warning',
  in_scope: 'info',
};

/** Said beside the declarations: the validator compares versions, it doesn't decide what a submission required. */
export const REGULATORY_APPLICABILITY_TEXT =
  'Whether these were the versions a particular submission required isn’t assessed: the validator compares what the study declares with what it supports, and the study start date alone doesn’t say which standard applied.';

/** Said once over the pre-checks, so none is read as the FDA's own check. */
export const TRC_PROXY_TEXT =
  "Pre-checks related to the FDA's Technical Rejection Criteria. Each checks part of a criterion from the upload alone; a confirmed criterion needs the eCTD package, which an upload doesn't carry. None blocks a submission, and none is counted as a finding.";

interface SendRunScopeProps {
  /** The run's scope; absent from a result stored before scope was read. */
  scope?: RunScope;
  trcProxies?: ReadonlyArray<SendTrcProxy>;
  /** After the run, or at the confirm step, where nothing has been checked yet. */
  stage?: SendScopeStage;
  testId?: string;
}

/**
 * Whether the run could validate the study at all, read from its Trial Summary: the state, with why, what
 * the study declared against what this validator supports, and the pre-checks related to the FDA's
 * Technical Rejection Criteria. Placed high in the report, since an out-of-scope study was not checked
 * and an unknown one was checked against an assumed version. A result stored before scope was read says
 * so, and is never shown as in scope.
 */
const SendRunScope: FC<SendRunScopeProps> = ({
  scope,
  trcProxies = [],
  stage = 'report',
  testId = 'invivo-send-validator__run-scope',
}) => {
  if (_isNil(scope)) {
    return (
      <p
        className="f6 dark-gray ma0 mb3"
        data-test-component="SendRunScope"
        data-test-element="scope-not-recorded"
        data-testid={testId}
      >
        {stage === 'confirm'
          ? "These files were detected before the validator read the study's Trial Summary, so whether the study is in scope isn't known."
          : "This result was stored before the validator read the study's Trial Summary, so it doesn't say whether the study was in scope."}
      </p>
    );
  }

  return (
    <div className="mb3" data-test-component="SendRunScope" data-test-key={scope.state} data-testid={testId}>
      <InlineBanner variant={VARIANT[scope.state]} title={scopeHeadlineOf(scope, stage)} testId={`${testId}__banner`}>
        {_isNotEmpty(scope.reasons) && (
          <ul className="list pl0 ma0 mt1" data-test-element="scope-reasons">
            {scope.reasons.map((reason) => (
              <li key={reason} className="f6 mt1" data-test-element="scope-reason" data-test-key={reason}>
                {SCOPE_REASON_TEXT[reason]}
              </li>
            ))}
          </ul>
        )}
        <dl className={styles.declarations} data-test-element="scope-declarations">
          {declarationsOf(scope).map((declaration) => (
            <div key={declaration.parameter} className={styles.declaration} data-test-key={declaration.parameter}>
              <dt>{DECLARATION_LABEL[declaration.parameter]}</dt>
              <dd>
                <span data-test-element="declared">{declaredText(declaration)}</span>
                {declaration.status !== 'not_declared' && declaration.status !== 'no_trial_summary' && (
                  <span className="mid-gray" data-test-element="declaration-status">
                    {' · '}
                    {DECLARATION_STATUS_TEXT[declaration.status]}
                  </span>
                )}
                {_isNotEmpty(declaration.supported_versions) && (
                  <span className="mid-gray" data-test-element="supported-versions">
                    {' · supported: '}
                    {declaration.supported_versions.join(', ')}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
        {scope.state === 'in_scope' && _isNil(scope.controlled_terminology_used) && (
          <p className="f6 near-black ma0 mt2" data-test-element="terminology-stood-down">
            {stage === 'confirm'
              ? `The terminology checks will stand down: the study declares terminology release ${scope.controlled_terminology.version ?? declaredText(scope.controlled_terminology)}, which this validator doesn't bundle. Every other check will run.`
              : `A partial result: the terminology checks stood down, because the study declares terminology release ${scope.controlled_terminology.version ?? declaredText(scope.controlled_terminology)}, which this validator doesn't bundle. Every other check ran, and its results stand.`}
          </p>
        )}
        <p className="f6 mid-gray ma0 mt2" data-test-element="regulatory-applicability">
          {REGULATORY_APPLICABILITY_TEXT}
        </p>
      </InlineBanner>

      {_isNotEmpty(trcProxies) && (
        <div className={styles.trcProxies} data-test-element="trc-proxies">
          <p className="f6 dark-gray ma0 mb2">{TRC_PROXY_TEXT}</p>
          <ul className="list pl0 ma0">
            {trcProxies.map((proxy) => (
              <li
                key={proxy.check}
                className={styles.trcProxy}
                data-test-element="trc-proxy"
                data-test-key={proxy.check}
              >
                <span className={styles.proxyLabel} data-test-element="trc-proxy-label">
                  Pre-check
                </span>
                <span className="f6 fw6" data-test-element="trc-proxy-outcome" data-test-key={proxy.outcome}>
                  {TRC_OUTCOME_TEXT[proxy.outcome]}
                </span>
                {_notNil(proxy.severity_level) && <SendSeverityBadge level={proxy.severity_level} />}
                <span className="f6 near-black" data-test-element="trc-proxy-message">
                  {proxy.message}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SendRunScope;
